/**
 * Announcements — zone contractions and market events.
 *
 * Both arrive with a `warnMs` because both are announced BEFORE they land.
 * GAME-DESIGN makes the 5-second warning a fairness rule, not a courtesy: a
 * mutator that lands unannounced is a roll a player could not price. So the
 * banner counts the warning down, out loud, and stays on screen for the whole
 * window.
 *
 * TWO REGISTERS, and they must never be confused. A market event is an
 * intervention — it gets the halt-notice board: a white header strip, the
 * cold-white shout, the pool's own line under it with the duration, pips for
 * the window, and the count. The zone is a rule of the map — it gets hazard
 * tape, flat ink and a cyan clock. Both are hueless, so the separation is
 * carried by shape and fill; the shipped version told them apart by a 1px
 * border colour, which is exactly the confusion the design forbids. The
 * markup differs here and the treatment lives in styles.css.
 *
 * Under the banners sits the CONDITIONS row: one chip per market condition
 * that is live right now, with its time left draining out of it. The banner
 * is gone in three seconds; low gravity runs for fifty. Without the row, a
 * player who joined the fight late (or looked away) has no idea why they are
 * floating — and nothing on the body says "40% gravity".
 */

import { playZoneWarnSound, playChaosSound } from '../audio/SoundManager.js';
import { escapeHtml } from './html.js';
import {
  chaosWorld,
  activeConditions,
  remainingMs,
  labelFor,
  describeKind,
  marketReadout,
} from '../arenas/market-state.js';

const FADE_MS = 300;

/** The pause bars either side of "Market event" on the board's header strip. */
const PAUSE_GLYPH =
  '<svg viewBox="0 0 10 10" aria-hidden="true"><rect x="1" y="1" width="3" height="8"/><rect x="6" y="1" width="3" height="8"/></svg>';

/** The bell either side of the countdown's label. */
const BELL_GLYPH =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1 C5 1 3.5 3.5 3.5 6 V9.5 L2 12 H14 L12.5 9.5 V6 C12.5 3.5 11 1 8 1 Z M6 13.5 A2 2 0 0 0 10 13.5 Z"/></svg>';

/** More pips than this and the row stops reading as a window. */
const MAX_PIPS = 10;

/** The conditions row refreshes at this rate; nothing on it changes faster. */
const CONDITIONS_TICK_MS = 100;
/** A condition blinks for its last three seconds. */
const ENDING_MS = 3000;

/**
 * The shout with a real ink stroke, as two layers: an ink copy stroked wide
 * underneath and the gradient face on top. The shipped `.shout-event` drew
 * the outline as FIVE chained drop-shadow filters on a 44 px gradient-clipped
 * word, re-rasterised every frame of the banner's entrance animation — a
 * measurable hitch on a phone the moment a market event lands. Two spans in
 * one grid cell cost nothing.
 */
function shoutMarkup(label, extraClass = '') {
  return (
    `<div class="shout ann-title ${extraClass}">` +
    `<span class="ann-title-ink" aria-hidden="true">${label}</span>` +
    `<span class="ann-title-face">${label}</span></div>`
  );
}

export class Announcements {
  constructor() {
    this.root = document.getElementById('announce-layer');
    this._live = [];
    this._condsEl = null;
    this._condRows = new Map();
    this._conds = [];
    this._readout = {};
    this._condClock = 0;
    this._tremorOn = false;
  }

  /**
   * @param {'zone'|'chaos'} kind
   * @param {string} text the label, verbatim from the pool
   * @param {number} [warnMs] 0 when the event has landed
   * @param {object|null} [mutation] the payload, for the pool's own line and the duration
   */
  push(kind, text, warnMs = 0, mutation = null) {
    const el = document.createElement('div');
    el.className = `announce ${kind}`;
    const secs = Math.ceil(warnMs / 1000);
    const label = escapeHtml(text);

    if (kind === 'chaos') {
      const pips = Array.from(
        { length: Math.min(MAX_PIPS, secs) },
        () => '<span class="ann-pip on"></span>'
      ).join('');
      const desc = describeKind(mutation?.kind);
      const runSecs = mutation?.durationMs ? Math.round(mutation.durationMs / 1000) : 0;
      el.innerHTML =
        `<div class="ann-strip">${PAUSE_GLYPH}<span>Market event</span>${PAUSE_GLYPH}</div>` +
        shoutMarkup(label) +
        (desc
          ? `<div class="ann-desc">${escapeHtml(desc)}${runSecs ? ` <b>${runSecs}s</b>` : ''}</div>`
          : '') +
        (warnMs > 0
          ? `<div class="ann-row"><div class="ann-pips" data-pips>${pips}</div>` +
            `<div class="ann-in"><span class="label">In</span><span class="shout ann-n" data-n>${secs}</span></div></div>`
          : '');
    } else {
      el.innerHTML =
        '<div class="ann-stripe"></div>' +
        `<div class="ann-body"><div class="shout shout-sm ann-title">${label}</div>` +
        (warnMs > 0 ? `<div class="ann-row"><span>in</span><b data-n>${secs}s</b></div>` : '') +
        '</div><div class="ann-stripe"></div>';
    }
    this.root.appendChild(el);

    if (kind === 'zone') playZoneWarnSound();
    else playChaosSound();

    this._live.push({
      el,
      kind,
      nEl: el.querySelector('[data-n]'),
      pips: Array.from(el.querySelectorAll('.ann-pip')),
      born: performance.now(),
      // A warned event stays up for its whole window plus a beat; an unwarned
      // one is a statement of fact and only needs long enough to read.
      life: warnMs > 0 ? warnMs + 900 : 2600,
      warnMs,
    });
    while (this._live.length > 3) this._live.shift().el.remove();
  }

  /**
   * One pip of the match-start ritual ("GET READY", "3", "2", "1", "FIGHT!").
   * Big, centred, and silent here — the beep is the caller's, timed with the
   * pip. Each pip replaces the previous so the count never stacks into a column.
   *
   * Every pip is gold: the opening bell is a money moment and the market has
   * not said anything yet. The `{ gold }` option callers still pass is accepted
   * and ignored so they need not change.
   */
  countdown(text) {
    for (let i = this._live.length - 1; i >= 0; i--) {
      if (this._live[i].kind !== 'countdown') continue;
      this._live[i].el.remove();
      this._live.splice(i, 1);
    }
    const el = document.createElement('div');
    el.className = 'announce countdown';
    // A digit gets the full 200px; a word would not fit at that size.
    const word = text.length > 2 ? ' word' : '';
    el.innerHTML =
      `<div class="label ann-bell">${BELL_GLYPH}<span>Opening bell</span>${BELL_GLYPH}</div>` +
      `<div class="shout shout-gold ann-count${word}">${escapeHtml(text)}</div>`;
    this.root.appendChild(el);
    this._live.push({ el, kind: 'countdown', born: performance.now(), life: 900, warnMs: 0 });
  }

  update() {
    const now = performance.now();
    for (let i = this._live.length - 1; i >= 0; i--) {
      const a = this._live[i];
      const age = now - a.born;

      if (a.warnMs > 0 && a.nEl) {
        const remaining = a.warnMs - age;
        const n = Math.ceil(remaining / 1000);
        const text = remaining > 0 ? (a.kind === 'chaos' ? String(n) : `${n}s`) : 'NOW';
        if (a.nEl.textContent !== text) a.nEl.textContent = text;
        // Pips go out from the left as the window closes; what is lit is what
        // is still to come.
        for (let p = 0; p < a.pips.length; p++) {
          a.pips[p].classList.toggle('on', p >= a.pips.length - Math.max(0, n));
        }
      }

      if (age > a.life + FADE_MS) {
        a.el.remove();
        this._live.splice(i, 1);
      } else if (age > a.life) {
        a.el.classList.add('fading');
      }
    }

    if (now - this._condClock >= CONDITIONS_TICK_MS) {
      this._condClock = now;
      this._updateConditions();
    }
  }

  /** The conditions row: reconciled by kind against the live ledger. */
  _updateConditions() {
    const world = chaosWorld();
    const conds = activeConditions(world, this._conds);
    if (!conds.length && !this._condsEl) {
      this._setTremor(false);
      return;
    }
    if (!this._condsEl) {
      this._condsEl = document.createElement('div');
      this._condsEl.className = 'ann-conds';
      this.root.appendChild(this._condsEl);
    }
    const seen = new Set();
    for (const c of conds) {
      seen.add(c.kind);
      let row = this._condRows.get(c.kind);
      if (!row) {
        const el = document.createElement('span');
        el.className = 'ann-cond';
        el.innerHTML = `<b>${escapeHtml(labelFor(c.kind))}</b><span class="t"></span>`;
        row = { el, tEl: el.querySelector('.t'), secs: -1 };
        this._condsEl.appendChild(el);
        this._condRows.set(c.kind, row);
      }
      const ms = remainingMs(c, world);
      const total = Math.max(1, c.untilTick - c.tick);
      const k = Math.max(0, Math.min(1, (c.untilTick - world.tick) / total));
      row.el.style.setProperty('--k', k.toFixed(3));
      const secs = Math.ceil(ms / 1000);
      if (secs !== row.secs) {
        row.secs = secs;
        row.tEl.textContent = `${secs}s`;
      }
      row.el.classList.toggle('ending', ms < ENDING_MS);
    }
    for (const [kind, row] of this._condRows) {
      if (seen.has(kind)) continue;
      row.el.remove();
      this._condRows.delete(kind);
    }
    if (this._condRows.size === 0) {
      this._condsEl.remove();
      this._condsEl = null;
    }
    // TREMOR shakes the board too: the one on-screen cue that survives a
    // static camera (spectating, the death cam).
    this._setTremor(marketReadout(world, this._readout).tremor > 0);
  }

  _setTremor(on) {
    if (on === this._tremorOn) return;
    this._tremorOn = on;
    this.root.classList.toggle('tremor', on);
  }

  /** Live condition labels, for tests and the debug overlay. */
  conditions() {
    return Array.from(this._condRows.keys());
  }

  clear() {
    for (const a of this._live) a.el.remove();
    this._live.length = 0;
    for (const row of this._condRows.values()) row.el.remove();
    this._condRows.clear();
    this._condsEl?.remove();
    this._condsEl = null;
    this._setTremor(false);
  }
}

/** Human labels for the zone events the sim emits. */
export function zoneLabel(targetRadius) {
  return targetRadius <= 0 ? 'THE ZONE CLOSES COMPLETELY' : 'THE ZONE IS CONTRACTING';
}
